import type { RecurrenceOverride, RecurrenceRule, Task } from "@app/core";
import { expandRecurringTask, fromIso, nextNOccurrences } from "@app/core";

const WINDOW_YEARS = 2;
const EMPTY: ReadonlyArray<RecurrenceOverride> = [];

/** Per-series memo of the next upcoming occurrence, keyed by recurrence-rule id.
 *  A hit is valid while the series' source-task / rule / override-array refs are
 *  unchanged AND the cached occurrence hasn't passed. Null misses are not cached:
 *  "none in the next two years" does not prove a sparse series has ended, and
 *  that occurrence can enter the rolling window as time advances. Hold one in a
 *  `useRef` and pass it to `tasksForList` so an edit to one task reuses every
 *  untouched series' answer instead of re-expanding them all over the window. */
export type NextInstanceCache = Map<
  string,
  { task: Task; rule: RecurrenceRule; overrides: ReadonlyArray<RecurrenceOverride>; next: Task }
>;

/**
 * Build the row set for the All Tasks list: non-recurring tasks pass through,
 * and each recurring series collapses to its single next upcoming occurrence
 * (earliest occurrence at/after `now`). Series with no occurrence left in the
 * window are dropped entirely — ended series are not listed. Events are
 * calendar-only and excluded by default; the Priorities view opts them in
 * (`includeEvents`) when the matrix scope covers events.
 */
export function tasksForList(
  tasks: ReadonlyArray<Task>,
  rules: Record<string, RecurrenceRule>,
  overrides: Record<string, ReadonlyArray<RecurrenceOverride>>,
  now: Date,
  cache?: NextInstanceCache,
  includeEvents = false,
): Task[] {
  const listable = tasks.filter((t) => includeEvents || !t.is_event);
  const recurring = listable.filter((t) => t.recurrence_id);
  if (recurring.length === 0) {
    cache?.clear();
    return listable.slice();
  }

  const nonRecurring = listable.filter((t) => !t.recurrence_id);
  const windowEnd = new Date(now.getTime());
  windowEnd.setFullYear(windowEnd.getFullYear() + WINDOW_YEARS);
  const nowIso = now.toISOString();

  const seenRules = new Set<string>();
  const nexts: Task[] = [];
  for (const t of recurring) {
    if (t.deleted_at || t.status !== "open") continue;
    const rule = rules[t.recurrence_id!];
    if (!rule) continue;
    const ov = overrides[rule.id] ?? EMPTY;
    seenRules.add(rule.id);

    // An ended series can never contribute a row, and misses are deliberately
    // not cached (see {@link NextInstanceCache}), so without this guard every
    // finished series re-expands over 30d → 180d → 2y on EVERY call. On a
    // long-lived calendar most series are finished, and that was the whole cost
    // of this function.
    if (hasEnded(rule, ov, now)) {
      cache?.delete(rule.id);
      continue;
    }
    const hit = cache?.get(rule.id);
    let next: Task | null;
    if (
      hit && hit.task === t && hit.rule === rule && hit.overrides === ov &&
      hit.next.due_at !== null && hit.next.due_at >= nowIso
    ) {
      next = hit.next;
    } else {
      next = nextUpcomingOccurrence(t, rule, ov, now, windowEnd);
      if (next) cache?.set(rule.id, { task: t, rule, overrides: ov, next });
      else cache?.delete(rule.id);
    }
    if (next) nexts.push(next);
  }
  if (cache) for (const id of cache.keys()) if (!seenRules.has(id)) cache.delete(id); // drop departed series

  return [...nonRecurring, ...nexts];
}

/**
 * Whether the series is definitively over, decided WITHOUT expanding it: its
 * `until` boundary has passed and no override can still place an occurrence
 * after it. A single override may have MOVED an occurrence past `until`, and a
 * `thisandfuture` override may have replaced the schedule shape outright, so
 * both veto the shortcut.
 */
function hasEnded(rule: RecurrenceRule, overrides: ReadonlyArray<RecurrenceOverride>, now: Date): boolean {
  if (!rule.until) return false;
  const until = fromIso(rule.until).getTime();
  if (!Number.isFinite(until) || until >= now.getTime()) return false;
  for (const override of overrides) {
    if (override.range === "thisandfuture" && override.rrule) return false;
    if (override.range !== "single" || override.status === "cancelled") continue;
    if (override.due_at && fromIso(override.due_at).getTime() >= now.getTime()) return false;
  }
  return true;
}

function nextUpcomingOccurrence(
  task: Task,
  rule: RecurrenceRule,
  overrides: ReadonlyArray<RecurrenceOverride>,
  now: Date,
  windowEnd: Date,
): Task | null {
  if (overrides.length === 0) {
    const [occ] = nextNOccurrences(rule, new Date(now.getTime() - 1), 1);
    if (!occ || occ > windowEnd) return null;
    return expandRecurringTask(task, rule, overrides, occ, occ)[0] ?? null;
  }

  const windowsDays = [30, 180, WINDOW_YEARS * 365];
  for (const days of windowsDays) {
    const end = new Date(Math.min(windowEnd.getTime(), now.getTime() + days * 86_400_000));
    let next: Task | null = null;
    for (const v of expandRecurringTask(task, rule, overrides, now, end)) {
      if (!v.due_at) continue;
      if (!next || !next.due_at || v.due_at < next.due_at) next = v;
    }
    if (next || end.getTime() >= windowEnd.getTime()) return next;
  }
  return null;
}
